import { SupportedTimeZones } from 'enums';
import { AM, DAY_IN_HOURS, HOUR_IN_MINUTES, PM } from 'consts';
import { timeZoneMappingService } from './TimeZoneMappingService';
import { calculateTimeDifference } from './utils';

import type {
  WorkingDayAmPmRange,
  // TimeZonesWithCountriesGroupedByOffset,
  TimeZonesMap,
} from 'types';
import { TimeZoneWithCountryData } from 'types';

class TimeZonesService {
  timeZonesList = Object.keys(SupportedTimeZones) as SupportedTimeZones[];

  getOverlappedTimeZones({
    sourceTimeZoneName,
    selectedMinutesOverlap,
    workingDayStartAmPm = { hours: 8, ampm: AM },
    workingDayEndAmPm = { hours: 6, ampm: PM },
  }: {
    sourceTimeZoneName: SupportedTimeZones | null;
    selectedMinutesOverlap: number | null;
    workingDayStartAmPm?: WorkingDayAmPmRange;
    workingDayEndAmPm?: WorkingDayAmPmRange;
  }) {
    if (sourceTimeZoneName === null || selectedMinutesOverlap === null)
      return null;

    const supportedTimeZonesData = this.timeZonesList.map((tzName) =>
      timeZoneMappingService.mapCorrectTimeZoneDataToDeprecated(tzName)
    );

    const matchedTzNamesWithOffset: TimeZonesMap = {};

    // answers the question: how long is the working day in minutes?
    const workingDayInMinutes = calculateTimeDifference({
      workingDayEndAmPm,
      workingDayStartAmPm,
    });
    const filteredTzData = supportedTimeZonesData.filter((tzData) => {
      const mappedComparisonTzName = timeZoneMappingService.mapTimeZoneNames(
        tzData.timezone().name
      ) as SupportedTimeZones;

      const match = this.isTimeZoneInSelectedRange({
        sourceTimeZoneName,
        comparisonTimeZoneName: mappedComparisonTzName,
        selectedMinutesOffset: selectedMinutesOverlap,
        workingDayInMinutes,
      });

      if (match.comparisonResult) {
        // lowercase is needed for better search capabilities
        matchedTzNamesWithOffset[mappedComparisonTzName.toLowerCase()] =
          match.geoTimeDifference;
      }

      return match.comparisonResult;
    });

    // todo this should be removed if we decide to remove old results view and replace it with table
    // const collectedTzDataWithCountries: TimeZonesWithCountriesGroupedByOffset =
    //   {};

    const timeZonesWithCountries: TimeZoneWithCountryData[] = [];

    filteredTzData.forEach((tzData) => {
      const mappedTzName = timeZoneMappingService.mapTimeZoneNames(
        tzData.timezone().name
      ) as SupportedTimeZones;

      timeZonesWithCountries.push({
        tzData,
        tzNameLabel: mappedTzName,
        country:
          timeZoneMappingService.getCorrectCountryDataByTimeZone(mappedTzName),
        overlapInHours:
          (workingDayInMinutes -
            matchedTzNamesWithOffset[mappedTzName.toLowerCase()]) /
          HOUR_IN_MINUTES,
      });
    });

    // filteredTzData.forEach((tzData) => {
    // const offset = tzData.offset();

    // const mappedTzName = timeZoneMappingService.mapTimeZoneNames(
    //   tzData.timezone().name
    // ) as SupportedTimeZones;
    //
    // const countriesData = {
    //   name: mappedTzName,
    //   countries:
    //     timeZoneMappingService.getCorrectCountriesDataByTimeZone(
    //       mappedTzName
    //     ),
    // };

    // if (collectedTzDataWithCountries[offset]) {
    //   collectedTzDataWithCountries[offset].push(countriesData);
    // } else {
    //   collectedTzDataWithCountries[offset] = [countriesData];
    // }
    // });

    return {
      // collectedTzDataWithCountries,
      matchedTzNamesWithOffset,
      timeZonesWithCountries,
    };
  }

  // by default we're checking working overlap based on source time zone's working hours (8AM - 6PM) means 10 hours
  private isTimeZoneInSelectedRange({
    sourceTimeZoneName,
    comparisonTimeZoneName,
    selectedMinutesOffset,
    workingDayInMinutes,
  }: {
    sourceTimeZoneName: SupportedTimeZones;
    comparisonTimeZoneName: SupportedTimeZones;
    selectedMinutesOffset: number;
    workingDayInMinutes: number;
  }) {
    if (selectedMinutesOffset === 0)
      return {
        comparisonResult: true,
        geoTimeDifference: 0,
      };

    const sourceTimeZoneDateData =
      timeZoneMappingService.mapCorrectTimeZoneDataToDeprecated(
        sourceTimeZoneName
      );

    const comparisonTimeZoneDateData =
      timeZoneMappingService.mapCorrectTimeZoneDataToDeprecated(
        comparisonTimeZoneName
      );

    const maxDifference = workingDayInMinutes - selectedMinutesOffset;

    // returns current hour in 24-hours format (e.g. 0, 12, 23)
    const sourceTimeZoneHour = sourceTimeZoneDateData.hour();
    const comparisonTimeZoneHour = comparisonTimeZoneDateData.hour();

    // spacetime has corner-case:
    // when timezones are close to each other geographically they might have 2-3h of real work time difference
    // but the date (month day number) will differ
    // spacetime does not have tools to handle this so we're getting the minimal difference using default value, with added 1 day and with subtracted 1 day
    const backwardDifference = Math.abs(
      sourceTimeZoneHour - comparisonTimeZoneHour
    );
    const forwardDifference =
      DAY_IN_HOURS - Math.abs(comparisonTimeZoneHour - sourceTimeZoneHour);

    // just to make it clear why we have backwardDifference and forwardDifference
    // let's say sourceTimeZoneHour is equal to 23 and comparisonTimeZoneHour is equal to 1
    // the forward difference would be 2 hours because 23 => 0 => 1
    // the backwards difference will be 22 because 23 => 22 => 21 => ... => 1
    // we want to find shortest difference since we're ignoring day number difference during overlap logic

    const minValue =
      Math.min(backwardDifference, forwardDifference) * HOUR_IN_MINUTES;

    const comparisonResult = minValue <= maxDifference;

    return { comparisonResult, geoTimeDifference: minValue };
  }
}

export const timeZonesService = new TimeZonesService();
